import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, addDoc, serverTimestamp, setDoc, doc, deleteDoc } from '../firebase';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { Plus, Tag, Trash2 } from 'lucide-react';
import { notify } from '../services/notify';
import ConfirmModal from './ConfirmModal';
import { auth } from '../firebase';

const getLocalAuthToken = () => localStorage.getItem('app_auth_token');
const isLocalSession = () => Boolean(getLocalAuthToken());
const getLocalApiHeaders = () => ({
  Authorization: `Bearer ${getLocalAuthToken()}`,
  'Content-Type': 'application/json'
});

export default function JournalsSetupView({ orgId, language }: { orgId: string, language: string }) {
  const [journals, setJournals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [newJournal, setNewJournal] = useState({
    name: '',
    code: '',
  });

  useEffect(() => {
    if (!orgId) return;

    if (isLocalSession()) {
      let cancelled = false;
      setLoading(true);
      void fetch(`/api/organizations/${orgId}/accounting-journals`, { headers: getLocalApiHeaders() })
        .then(async response => {
          const payload = await response.json().catch(() => null);
          if (!response.ok) throw new Error(payload?.error || 'Could not load accounting journals');
          return payload as any[];
        })
        .then(loadedJournals => {
          if (!cancelled) setJournals(loadedJournals);
        })
        .catch(error => {
          console.error('Local API error loading accounting journals:', error);
          if (!cancelled) notify.error(error instanceof Error ? error.message : 'Failed to load accounting journals');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => { cancelled = true; };
    }

    const q = query(collection(db, 'accounting_journals'), where('orgId', '==', orgId));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setJournals(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'accounting_journals');
    });
    return () => unsubscribe();
  }, [orgId]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isLocalSession()) {
        const response = await fetch(`/api/organizations/${orgId}/accounting-journals`, {
          method: 'POST',
          headers: getLocalApiHeaders(),
          body: JSON.stringify(newJournal)
        });
        const saved = await response.json().catch(() => null);
        if (!response.ok || !saved) throw new Error(saved?.error || 'Could not create accounting journal');
        setJournals(current => [...current, saved].sort((left, right) => left.name.localeCompare(right.name)));
      } else {
        await addDoc(collection(db, 'accounting_journals'), {
          ...newJournal,
          orgId,
          createdBy: auth.currentUser?.uid,
          createdAt: serverTimestamp()
        });
      }
      setIsModalOpen(false);
      setNewJournal({ name: '', code: '' });
      notify.confirm('Journal created successfully');
    } catch (err) {
      if (isLocalSession()) {
        console.error('Local API error creating accounting journal:', err);
        notify.error(err instanceof Error ? err.message : 'Failed to create accounting journal');
      } else {
        handleFirestoreError(err, OperationType.CREATE, 'accounting_journals');
      }
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      if (isLocalSession()) {
        const response = await fetch(`/api/organizations/${orgId}/accounting-journals/${deleteId}`, {
          method: 'DELETE',
          headers: getLocalApiHeaders()
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => null);
          throw new Error(payload?.error || 'Could not delete accounting journal');
        }
        setJournals(current => current.filter(journal => journal.id !== deleteId));
      } else {
        await deleteDoc(doc(db, 'accounting_journals', deleteId));
      }
      setDeleteId(null);
      notify.confirm('Journal deleted successfully');
    } catch (err) {
      if (isLocalSession()) {
        console.error('Local API error deleting accounting journal:', err);
        notify.error(err instanceof Error ? err.message : 'Failed to delete accounting journal');
      } else {
        handleFirestoreError(err, OperationType.DELETE, 'accounting_journals');
      }
    }
  };

  if (loading) return <div className="p-6">Loading journals...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-end">
        <div className="space-y-1">
          <h1 className="text-4xl font-bold tracking-tighter text-[#141414] uppercase">Journals Setup</h1>
          <p className="text-sm font-mono text-[#141414]/60 uppercase tracking-widest">Manage accounting journals</p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="bg-[#141414] text-white px-6 py-3 rounded-2xl font-bold uppercase tracking-widest hover:bg-black/80 transition-all flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add Journal
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {journals.map(journal => (
          <div key={journal.id} className="p-6 rounded-3xl border-2 border-[#141414]/10 hover:border-[#141414] transition-all bg-white relative group">
            <h3 className="font-bold text-xl uppercase">{journal.name}</h3>
            {journal.code && <p className="text-sm font-mono text-[#141414]/60 mt-2">{journal.code}</p>}
            
            <button
              onClick={() => setDeleteId(journal.id)}
              className="absolute top-4 right-4 p-2 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 rounded-xl"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        {journals.length === 0 && (
          <div className="col-span-full p-12 text-center border-2 border-dashed border-[#141414]/10 rounded-3xl">
            <p className="text-sm uppercase tracking-widest font-bold opacity-50">No journals found.</p>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overscroll-contain p-4 bg-black/50 backdrop-blur-sm"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsModalOpen(false);
          }}
        >
          <div className="bg-white rounded-[2rem] w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain border border-[#141414]/10 shadow-2xl">
            <div className="p-8 space-y-8">
              <div className="space-y-2 text-center">
                <h2 className="text-3xl font-bold tracking-tighter uppercase">Add Journal</h2>
                <p className="text-sm font-mono text-[#141414]/60 uppercase tracking-widest">Create a new journal book</p>
              </div>

              <form onSubmit={handleSave} className="space-y-6">
                <div className="space-y-1">
                  <label className="text-[10px] uppercase font-bold tracking-widest opacity-50">Journal Name</label>
                  <input
                    required
                    type="text"
                    className="w-full bg-[#141414]/5 border-none p-4 rounded-2xl outline-none focus:ring-2 focus:ring-[#141414]/20 transition-all font-bold"
                    placeholder="e.g. Sales Journal"
                    value={newJournal.name}
                    onChange={(e) => setNewJournal({ ...newJournal, name: e.target.value })}
                  />
                </div>
                
                <div className="space-y-1">
                  <label className="text-[10px] uppercase font-bold tracking-widest opacity-50">Short Code</label>
                  <input
                    type="text"
                    className="w-full bg-[#141414]/5 border-none p-4 rounded-2xl outline-none focus:ring-2 focus:ring-[#141414]/20 transition-all font-bold"
                    placeholder="e.g. SLJ"
                    value={newJournal.code}
                    onChange={(e) => setNewJournal({ ...newJournal, code: e.target.value })}
                  />
                </div>

                <div className="pt-4 flex gap-4">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="flex-1 py-4 border border-[#141414]/10 rounded-2xl font-bold uppercase tracking-widest hover:bg-[#141414]/5 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-4 bg-[#141414] text-white rounded-2xl font-bold uppercase tracking-widest hover:bg-black/80 transition-all"
                  >
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <ConfirmModal
          isOpen={!!deleteId}
          title="Delete Journal"
          message="Are you sure you want to delete this journal?"
          onConfirm={handleDelete}
          onClose={() => setDeleteId(null)}
        />
      )}
    </div>
  );
}
